import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { type Hit, searchQueryOptions } from '@/entities/search'
import { TagChips } from '@/entities/tag'
import { pluralRu, useDebouncedValue } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { HitRow } from '@/widgets/command-palette'

const searchSchema = z.object({
  q: z.string().optional().catch(undefined),
  // Тег уточняет выдачу и живёт независимо от текста запроса — переживает и
  // правку запроса, и перезагрузку.
  tag: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_app/search')({
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: SearchPage,
})

const SEARCH_DEBOUNCE_MS = 300

interface HitLinkProps {
  hit: Hit
  className: string
  children: ReactNode
}

// Роуты типизированы, поэтому вид хита раскладываем в конкретный роут (hit.url
// ведёт туда же, но нетипизированной строкой).
function HitLink({ hit, className, children }: HitLinkProps) {
  switch (hit.kind) {
    case 'company':
      return (
        <Link to="/companies/$companyId" params={{ companyId: hit.id }} className={className}>
          {children}
        </Link>
      )
    case 'project':
      return (
        <Link to="/projects/$projectId" params={{ projectId: hit.id }} className={className}>
          {children}
        </Link>
      )
    default:
      return (
        <Link to="/events/$eventId" params={{ eventId: hit.id }} className={className}>
          {children}
        </Link>
      )
  }
}

function SearchPage() {
  const { q = '', tag } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [input, setInput] = useState(q)
  const inputRef = useRef<HTMLInputElement>(null)

  // Внешний переход (палитра, ссылка) на уже смонтированную страницу меняет q в
  // URL — поле догоняет его без каскадного эффекта.
  const [prevQ, setPrevQ] = useState(q)
  if (q !== prevQ) {
    setPrevQ(q)
    setInput(q)
  }

  const trimmed = useDebouncedValue(input.trim(), SEARCH_DEBOUNCE_MS)
  const search = useQuery({
    ...searchQueryOptions(trimmed, tag ? { tag } : {}),
    enabled: trimmed.length > 0,
  })

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Запрос и тег держим в URL: выдача шарится ссылкой и переживает перезагрузку.
  // replace — печать не должна засорять историю.
  const apply = (next: { q?: string; tag?: string }) =>
    void navigate({
      search: { ...(next.q?.trim() ? { q: next.q } : {}), ...(next.tag ? { tag: next.tag } : {}) },
      replace: true,
    })

  const hits = search.data ?? []
  // Бэк требует непустой запрос (SEARCH_EMPTY_QUERY); тег его не заменяет.
  const ready = trimmed.length > 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Поиск"
        description="По событиям, компаниям, проектам и их тегам. Точная фраза — в кавычках, «-» исключает слово."
      />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-gutter pb-safe-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 rounded-control border border-border-default bg-bg-surface px-4 focus-within:border-border-accent">
            <Icon name="search" size={18} className="shrink-0 text-text-tertiary" />
            <input
              ref={inputRef}
              type="text"
              aria-label="Поисковый запрос"
              autoComplete="off"
              value={input}
              placeholder="Что ищем?"
              onChange={(e) => {
                setInput(e.target.value)
                apply({ q: e.target.value, ...(tag ? { tag } : {}) })
              }}
              // text-base на телефоне: поле мельче 16px заставляет iOS зумить
              // страницу при фокусе, и вернуться к прежнему масштабу нечем.
              className="min-w-0 flex-1 bg-transparent py-3 text-base text-text-primary placeholder:text-text-tertiary focus:outline-none sm:text-sm"
            />
          </div>

          {tag && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-text-tertiary text-xs">Фильтр по тегу:</span>
              <Chip variant="soft" className="p-0">
                <button
                  type="button"
                  onClick={() => apply({ q: input })}
                  aria-label={`Снять фильтр по тегу ${tag}`}
                  className="flex items-center gap-1.5 px-2.5 py-1 transition-colors hover:text-text-primary"
                >
                  {tag}
                  <Icon name="close" size={12} />
                </button>
              </Chip>
            </div>
          )}

          <div className="mt-6">
            {!ready ? (
              <p className="px-1 py-8 text-sm text-text-tertiary">
                {tag
                  ? `Введите запрос — фильтр по тегу «${tag}» применится к результатам.`
                  : 'Введите запрос — ищем по названиям и описаниям событий, названиям клиентов и проектов, тегам.'}
              </p>
            ) : search.isPending ? (
              <div className="flex justify-center py-16">
                <Spinner />
              </div>
            ) : search.isError ? (
              <div className="flex flex-col items-start gap-3 px-1 py-8">
                <p className="text-sm text-text-tertiary">Поиск недоступен.</p>
                <Button variant="secondary" size="sm" onClick={() => void search.refetch()}>
                  Повторить
                </Button>
              </div>
            ) : hits.length === 0 ? (
              <EmptyState
                title="Ничего не нашлось"
                description="Проверьте запрос или попробуйте другие слова."
              />
            ) : (
              <div className="flex flex-col gap-1">
                <p className="px-1 pb-2 text-text-tertiary text-xs">
                  Найдено: <span className="mono-num">{hits.length}</span>{' '}
                  {pluralRu(hits.length, 'результат', 'результата', 'результатов')}
                </p>
                {hits.map((hit) => (
                  <div
                    key={`${hit.kind}:${hit.id}`}
                    className="flex items-start gap-2 rounded-control transition-colors hover:bg-bg-raised"
                  >
                    <HitLink hit={hit} className="flex min-w-0 flex-1 items-start gap-3 px-3 py-2">
                      <HitRow hit={hit} />
                    </HitLink>
                    {/* Тег-хит: сниппет — имя сработавшего тега. Чип вне ссылки —
                        интерактив внутри интерактива ломает a11y. */}
                    {hit.matchedIn === 'tag' && hit.snippet && (
                      <TagChips
                        tags={[hit.snippet]}
                        onTagClick={(next) => apply({ q: input, tag: next })}
                        className="flex shrink-0 items-center py-2 pr-2"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
